import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  collection,
  doc,
  getDocs,
  query,
  where,
  onSnapshot,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";

import { db } from "../firebase";
import ForexWebSocket from "../services/ForexWebSocket";
import VolatilityHeader from "./VolatilityHeader";
import BottomNavVolatility from "./BottomNavVolatility";
import "./Bulk.css";

const MARKETS = [
  ["volatility10", "R_10", "Volatility 10"],
  ["volatility25", "R_25", "Volatility 25"],
  ["volatility50", "R_50", "Volatility 50"],
  ["volatility75", "R_75", "Volatility 75"],
  ["volatility100", "R_100", "Volatility 100"],
  ["volatility10-1s", "1HZ10V", "Volatility 10 (1s)"],
  ["volatility25-1s", "1HZ25V", "Volatility 25 (1s)"],
  ["volatility50-1s", "1HZ50V", "Volatility 50 (1s)"],
  ["volatility75-1s", "1HZ75V", "Volatility 75 (1s)"],
  ["volatility100-1s", "1HZ100V", "Volatility 100 (1s)"],
].map(([id, symbol, name]) => ({
  id,
  symbol,
  name,
  fallbackDecimals: 2,
}));

const decimals = (pip, fallback = 2) =>
  Number.isInteger(Number(pip)) &&
  Number(pip) >= 0 &&
  Number(pip) <= 10
    ? Number(pip)
    : fallback;

const lastDigit = (price, pip, fallback = 2) => {
  const n = Number(price);

  if (!Number.isFinite(n)) return 0;

  const s = n
    .toFixed(decimals(pip, fallback))
    .replace(/\D/g, "");

  return Number(s.slice(-1)) || 0;
};
const moneyPrice = (price, pip = 2) =>
  Number.isFinite(Number(price))
    ? Number(price).toLocaleString("en-US", {
        minimumFractionDigits: decimals(pip),
        maximumFractionDigits: decimals(pip),
      })
    : "—";

const stamp = (value) =>
  typeof value?.toMillis === "function"
    ? value.toMillis()
    : new Date(value || 0).getTime() || 0;

export default function Bulk() {
  const selectedRef = useRef("volatility10-1s");
  const historyReq = useRef(null);
  const mounted = useRef(false);
  const prevPrice = useRef(null);

  const [marketId, setMarketId] = useState("volatility10-1s");
  const [tradeType, setTradeType] = useState("over-under");
  const [ticks, setTicks] = useState(1);
  const [prediction, setPrediction] = useState(5);
  const [stake, setStake] = useState(0.5);
  const [bulkCount, setBulkCount] = useState(5);

  const [price, setPrice] = useState(null);
  const [pip, setPip] = useState(2);
  const [epoch, setEpoch] = useState(null);
  const [live, setLive] = useState({});
  const [digits, setDigits] = useState([]);
  const [recent, setRecent] = useState([]);

  const [status, setStatus] = useState("connecting");
  const [error, setError] = useState("");
  const [balance, setBalance] = useState(0);
  const [username, setUsername] = useState(
    () => localStorage.getItem("username") || ""
  );

  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [running, setRunning] = useState(false);
  const [message, setMessage] = useState("");
  const [tab, setTab] = useState("transactions");

  const market = useMemo(
    () => MARKETS.find((item) => item.id === marketId) || MARKETS[5],
    [marketId]
  );

  const currentDigit = lastDigit(
    price,
    pip,
    market.fallbackDecimals
  );

  const risk = Number(stake) * Number(bulkCount);
  const atRisk = Number.isFinite(risk) ? risk : 0;

  const stats = useMemo(() => {
    const counts = Array(10).fill(0);

    digits.forEach((digit) => {
      if (digit >= 0 && digit <= 9) counts[digit]++;
    });

    const total = digits.length || 1;

    return counts.map((count, index) => ({
      digit: index,
      percent: (count / total) * 100,
    }));
  }, [digits]);

  /* =====================================================
     USER
  ===================================================== */

  const getUser = useCallback(async () => {
    const currentUsername = (
      localStorage.getItem("username") || ""
    ).trim();

    if (!currentUsername) return null;

    const usersQuery = query(
      collection(db, "users"),
      where("username", "==", currentUsername)
    );

    const snapshot = await getDocs(usersQuery);

    if (snapshot.empty) return null;

    const userDoc = snapshot.docs[0];
    const data = userDoc.data();

    return {
      id: userDoc.id,
      username: currentUsername,
      data,
      balance: Number(data.usdtBalanceOther) || 0,
    };
  }, []);

  const refreshBalance = useCallback(async () => {
    try {
      const user = await getUser();

      if (user) {
        setUsername(user.username);
        setBalance(user.balance);
      } else {
        setBalance(0);
      }
    } catch (err) {
      console.error("Balance error:", err);
    }
  }, [getUser]);

  useEffect(() => {
    refreshBalance();
  }, [refreshBalance]);

  /* =====================================================
     FIRESTORE ORDERS
  ===================================================== */

  useEffect(() => {
    const currentUsername = (
      localStorage.getItem("username") || ""
    ).trim();

    if (!currentUsername) {
      setOrders([]);
      setLoadingOrders(false);
      return;
    }

    const ordersQuery = query(
      collection(db, "volatilityorder"),
      where("username", "==", currentUsername)
    );

    const unsubscribe = onSnapshot(
      ordersQuery,
      (snapshot) => {
        const rows = snapshot.docs.map((item) => ({
          id: item.id,
          ...item.data(),
        }));

        rows.sort(
          (a, b) => stamp(b.createdAt) - stamp(a.createdAt)
        );

        setOrders(rows);
        setLoadingOrders(false);
      },
      (err) => {
        console.error("Orders snapshot error:", err);
        setLoadingOrders(false);
      }
    );

    return unsubscribe;
  }, []);
    /* =====================================================
     WEBSOCKET / LIVE MARKET TICKS
  ===================================================== */

  useEffect(() => {
    let active = true;

    const unsubscribeConnection =
      ForexWebSocket.onConnection((data) => {
        if (!active) return;

        setStatus(
          data.connected
            ? "connected"
            : data.connecting
            ? "connecting"
            : "disconnected"
        );

        if (data.error) {
          setError(
            typeof data.error === "string"
              ? data.error
              : data.error.message || "WebSocket error"
          );
        } else if (data.connected) {
          setError("");
        }
      });

    const unsubscribeTicks =
      ForexWebSocket.onTick((data) => {
        if (
          !active ||
          data?.msg_type !== "tick" ||
          !data.tick
        ) {
          return;
        }

        const tick = data.tick;

        const foundMarket = MARKETS.find(
          (item) => item.symbol === tick.symbol
        );

        if (!foundMarket) return;

        const nextPrice = Number(tick.quote);

        if (!Number.isFinite(nextPrice)) return;

        const nextEpoch = Number(tick.epoch) || null;

        const nextPip = decimals(
          tick.pip_size,
          foundMarket.fallbackDecimals
        );

        setLive((current) => ({
          ...current,
          [foundMarket.id]: {
            price: nextPrice,
            epoch: nextEpoch,
            pip: nextPip,
          },
        }));

        // Update display only for the selected market.
        if (selectedRef.current !== foundMarket.id) {
          return;
        }

        setPrice(nextPrice);
        setEpoch(nextEpoch);
        setPip(nextPip);

        const digit = lastDigit(
          nextPrice,
          nextPip,
          foundMarket.fallbackDecimals
        );

        setDigits((current) =>
          [digit, ...current].slice(0, 100)
        );

        setRecent((current) =>
          [
            {
              price: nextPrice,
              digit,
              epoch: nextEpoch,
            },
            ...current,
          ].slice(0, 20)
        );

        prevPrice.current = nextPrice;
      });

    ForexWebSocket.connect();

    // Subscribe to all listed market symbols.
    MARKETS.forEach((item) => {
      ForexWebSocket.ticks(item.symbol);
    });

    return () => {
      active = false;

      unsubscribeConnection();
      unsubscribeTicks();

      // Do not close the shared WebSocket singleton here,
      // because other components may also be using it.
    };
  }, []);

  /* =====================================================
     CHANGE MARKET
  ===================================================== */

  const handleMarketChange = (nextMarketId) => {
    const nextMarket = MARKETS.find(
      (item) => item.id === nextMarketId
    );

    if (!nextMarket) return;

    selectedRef.current = nextMarketId;

    setMarketId(nextMarketId);
    setDigits([]);
    setRecent([]);

    const cached = live[nextMarketId];

    setPrice(cached?.price ?? null);
    setEpoch(cached?.epoch ?? null);
    setPip(
      cached?.pip ?? nextMarket.fallbackDecimals
    );

    prevPrice.current = cached?.price ?? null;
    setMessage("");
    setError("");
  };

  /* =====================================================
     DISPLAY HELPERS
  ===================================================== */

  const formatTime = (value) => {
    if (!value) return "--:--:--";

    return new Date(value * 1000).toLocaleTimeString();
  };

  const formatPrice = (value) => {
    if (!Number.isFinite(Number(value))) {
      return "—";
    }

    return moneyPrice(value, pip);
  };

  const connectionLabel =
    status === "connected"
      ? "Connected"
      : status === "connecting"
      ? "Connecting..."
      : status === "error"
      ? "Connection error"
      : "Disconnected";
  /* =====================================================
     VALIDATION YA BULK TRADES
  ===================================================== */

  const validateBatch = useCallback(() => {
    const currentUsername = (
      localStorage.getItem("username") || ""
    ).trim();

    const stakeValue = Number(stake);
    const countValue = Number(bulkCount);
    const predictionValue = Number(prediction);
    const ticksValue = Number(ticks);

    if (!currentUsername) {
      throw new Error(
        "Tafadhali ingia kwenye akaunti yako kwanza."
      );
    }

    if (
      !Number.isFinite(stakeValue) ||
      stakeValue <= 0
    ) {
      throw new Error(
        "Stake lazima iwe zaidi ya sifuri."
      );
    }

    if (
      !Number.isInteger(countValue) ||
      countValue < 1 ||
      countValue > 100
    ) {
      throw new Error(
        "Idadi ya trades lazima iwe kati ya 1 na 100."
      );
    }

    if (
      !Number.isInteger(predictionValue) ||
      predictionValue < 0 ||
      predictionValue > 9
    ) {
      throw new Error(
        "Prediction lazima iwe digit kati ya 0 na 9."
      );
    }

    if (
      !Number.isInteger(ticksValue) ||
      ticksValue < 1 ||
      ticksValue > 10
    ) {
      throw new Error(
        "Ticks lazima ziwe kati ya 1 na 10."
      );
    }

    if (!Number.isFinite(Number(price))) {
      throw new Error(
        "Subiri live price ipatikane kwanza."
      );
    }

    if (status !== "connected") {
      throw new Error(
        "WebSocket haijaunganishwa bado."
      );
    }

    if (stakeValue * countValue > balance) {
      throw new Error(
        "Balance haitoshi kwa jumla ya trades ulizochagua."
      );
    }

    return {
      username: currentUsername,
      stake: Number(stakeValue.toFixed(2)),
      count: countValue,
      prediction: predictionValue,
      ticks: ticksValue,
      market,
      entryPrice: Number(price),
      entryDigit: currentDigit,
      entryEpoch: epoch || Math.floor(Date.now() / 1000),
    };
  }, [
    stake,
    bulkCount,
    prediction,
    ticks,
    price,
    status,
    balance,
    market,
    currentDigit,
    epoch,
  ]);

  /* =====================================================
     CREATE BULK ORDERS
  ===================================================== */

  const fireBatch = useCallback(async () => {
    if (running) return;

    setMessage("");
    setError("");

    let batch;

    try {
      batch = validateBatch();
    } catch (err) {
      setError(err.message || "Taarifa za trade si sahihi.");
      return;
    }

    setRunning(true);
    setMessage("Inaandaa trades...");

    try {
      const user = await getUser();

      if (!user) {
        throw new Error(
          "Akaunti ya mtumiaji haijapatikana."
        );
      }

      const totalStake = Number(
        (batch.stake * batch.count).toFixed(2)
      );

      const userRef = doc(db, "users", user.id);

      const orderRefs = Array.from(
        { length: batch.count },
        () => doc(collection(db, "volatilityorder"))
      );

      let finalBalance = 0;

      await runTransaction(db, async (transaction) => {
        const userSnapshot = await transaction.get(userRef);

        if (!userSnapshot.exists()) {
          throw new Error("USER_NOT_FOUND");
        }

        const userData = userSnapshot.data();
        const firestoreBalance =
          Number(userData.usdtBalanceOther) || 0;

        if (firestoreBalance < totalStake) {
          throw new Error("INSUFFICIENT_BALANCE");
        }

        finalBalance = Number(
          (firestoreBalance - totalStake).toFixed(2)
        );

        transaction.update(userRef, {
          usdtBalanceOther: finalBalance,
        });

        orderRefs.forEach((orderRef, index) => {
          transaction.set(orderRef, {
            username: batch.username,
            userId: user.id,
            orderType: "VOLATILITY",
            status: "OPEN",

            batchCount: batch.count,
            batchIndex: index + 1,

            tradeType,
            contractType: tradeType,

            marketId: batch.market.id,
            marketSymbol: batch.market.symbol,
            marketName: batch.market.name,

            stake: batch.stake,
            payout: 0,
            profit: 0,
            result: 0,

            prediction: batch.prediction,
            ticks: batch.ticks,

            entryPrice: batch.entryPrice,
            entryDigit: batch.entryDigit,
            entryTick: batch.entryEpoch,

            exitPrice: null,
            exitDigit: null,
            exitTick: null,

            balanceBefore: Number(
              (
                firestoreBalance -
                batch.stake * index
              ).toFixed(2)
            ),

            balanceAfter: Number(
              (
                firestoreBalance -
                batch.stake * (index + 1)
              ).toFixed(2)
            ),

            currency: "USDT",
            tradingMode: "bulk",

            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            closedAt: null,
          });
        });
      });

      setBalance(finalBalance);
      setMessage(
        `${batch.count} orders zimehifadhiwa. Hii haimaanishi kuwa zimefungwa au matokeo yamehakikishwa.`
      );
    } catch (err) {
      console.error("Bulk order error:", err);

      if (err.message === "INSUFFICIENT_BALANCE") {
        setError("Balance haitoshi. Hakuna orders zilizofunguliwa.");
      } else if (err.message === "USER_NOT_FOUND") {
        setError("Akaunti haipatikani.");
      } else {
        setError(
          err.message || "Imeshindikana kuandaa bulk orders."
        );
      }
    } finally {
      setRunning(false);
    }
  }, [
    running,
    validateBatch,
    getUser,
    tradeType,
  ]);

  /* =====================================================
     RESET FORM
  ===================================================== */

  const reset = useCallback(() => {
    if (running) return;

    setStake(0.5);
    setBulkCount(5);
    setPrediction(5);
    setTicks(1);
    setTradeType("over-under");
    setMessage("");
    setError("");
  }, [running]);
    /* =====================================================
     UI — BULK TRADING
  ===================================================== */

  return (
    <div className="bulk-page">
      <VolatilityHeader />

      <main className="bulk-container">
        <div className="bulk-heading">
          <div>
            <h1>Bulk Trading</h1>
            <p>
              Sanidi orders nyingi kwa wakati mmoja.
            </p>
          </div>

          <span
            className={`bulk-status bulk-status-${status}`}
          >
            <span className="bulk-status-dot" />
            {connectionLabel}
          </span>
        </div>

        {error && (
          <div className="bulk-alert bulk-alert-error">
            {error}
          </div>
        )}

        {message && (
          <div className="bulk-alert bulk-alert-info">
            {message}
          </div>
        )}

        <section className="bulk-summary-grid">
          <div className="bulk-card">
            <span>Account Balance</span>
            <strong>
              ${Number(balance).toFixed(2)}
            </strong>
            <small>USDT</small>
          </div>

          <div className="bulk-card">
            <span>Market Price</span>
            <strong>
              {formatPrice(price)}
            </strong>
            <small>{market.name}</small>
          </div>

          <div className="bulk-card">
            <span>Current Digit</span>
            <strong>
              {price == null ? "—" : currentDigit}
            </strong>
            <small>
              Tick: {formatTime(epoch)}
            </small>
          </div>

          <div className="bulk-card">
            <span>Total Stake</span>
            <strong>
              ${atRisk.toFixed(2)}
            </strong>
            <small>
              {Number(bulkCount) || 0} orders
            </small>
          </div>
        </section>

        <section className="bulk-panel">
          <div className="bulk-panel-heading">
            <h2>Trade Settings</h2>

            <button
              type="button"
              className="bulk-button bulk-button-secondary"
              onClick={reset}
              disabled={running}
            >
              Reset
            </button>
          </div>

          <div className="bulk-form-grid">
            <label className="bulk-field">
              <span>Market</span>

              <select
                value={marketId}
                onChange={(event) =>
                  handleMarketChange(event.target.value)
                }
                disabled={running}
              >
                {MARKETS.map((item) => (
                  <option
                    key={item.id}
                    value={item.id}
                  >
                    {item.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="bulk-field">
              <span>Contract Type</span>

              <select
                value={tradeType}
                onChange={(event) =>
                  setTradeType(event.target.value)
                }
                disabled={running}
              >
                <option value="over-under">
                  Over / Under
                </option>
                <option value="even-odd">
                  Even / Odd
                </option>
                <option value="matches-differs">
                  Matches / Differs
                </option>
              </select>
            </label>

            <label className="bulk-field">
              <span>Stake per Order (USDT)</span>

              <input
                type="number"
                min="0.01"
                step="0.01"
                value={stake}
                onChange={(event) =>
                  setStake(event.target.value)
                }
                disabled={running}
              />
            </label>

            <label className="bulk-field">
              <span>Number of Orders</span>

              <input
                type="number"
                min="1"
                max="100"
                step="1"
                value={bulkCount}
                onChange={(event) =>
                  setBulkCount(event.target.value)
                }
                disabled={running}
              />
            </label>

            <label className="bulk-field">
              <span>Prediction Digit</span>

              <select
                value={prediction}
                onChange={(event) =>
                  setPrediction(Number(event.target.value))
                }
                disabled={running}
              >
                {Array.from(
                  { length: 10 },
                  (_, digit) => (
                    <option
                      key={digit}
                      value={digit}
                    >
                      {digit}
                    </option>
                  )
                )}
              </select>
            </label>

            <label className="bulk-field">
              <span>Ticks</span>

              <select
                value={ticks}
                onChange={(event) =>
                  setTicks(Number(event.target.value))
                }
                disabled={running}
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(
                  (value) => (
                    <option
                      key={value}
                      value={value}
                    >
                      {value} {value === 1 ? "tick" : "ticks"}
                    </option>
                  )
                )}
              </select>
            </label>
          </div>

          <div className="bulk-risk-summary">
            <div>
              <span>Total stake</span>
              <strong>
                ${atRisk.toFixed(2)} USDT
              </strong>
            </div>

            <div>
              <span>Balance after reservation</span>
              <strong>
                $
                {(
                  Number(balance) - atRisk
                ).toFixed(2)}{" "}
                USDT
              </strong>
            </div>
          </div>

          <button
            type="button"
            className="bulk-button bulk-button-primary bulk-submit"
            onClick={fireBatch}
            disabled={
              running ||
              status !== "connected" ||
              !Number.isFinite(Number(price)) ||
              Number(stake) <= 0 ||
              Number(bulkCount) < 1 ||
              atRisk > balance
            }
          >
            {running
              ? "Processing..."
              : `Prepare ${Number(bulkCount) || 0} Orders`}
          </button>

          <p className="bulk-disclaimer">
            Kitufe hiki huandaa records za orders kwenye
            Firestore. Hakihakikishi utekelezaji wa contract
            au matokeo ya soko.
          </p>
        </section>

        <section className="bulk-panel">
          <div className="bulk-panel-heading">
            <div>
              <h2>Orders</h2>
              <p>
                Orders zilizohifadhiwa kwenye akaunti hii.
              </p>
            </div>

            <span className="bulk-count-badge">
              {orders.length}
            </span>
          </div>

          {loadingOrders ? (
            <div className="bulk-empty-state">
              Inapakia orders...
            </div>
          ) : orders.length === 0 ? (
            <div className="bulk-empty-state">
              Hakuna orders bado.
            </div>
          ) : (
            <div className="bulk-table-wrap">
              <table className="bulk-table">
                <thead>
                  <tr>
                    <th>Market</th>
                    <th>Contract</th>
                    <th>Stake</th>
                    <th>Entry Price</th>
                    <th>Status</th>
                    <th>Created</th>
                  </tr>
                </thead>

                <tbody>
                  {orders.map((order) => (
                    <tr key={order.id}>
                      <td>
                        <strong>
                          {order.marketName || "—"}
                        </strong>
                        <small>
                          {order.marketSymbol || ""}
                        </small>
                      </td>

                      <td>
                        {order.contractType ||
                          order.tradeType ||
                          "—"}
                      </td>

                      <td>
                        $
                        {Number(order.stake || 0).toFixed(2)}
                      </td>

                      <td>
                        {order.entryPrice == null
                          ? "—"
                          : Number(
                              order.entryPrice
                            ).toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 10,
                            })}
                      </td>

                      <td>
                        <span
                          className={`bulk-order-status bulk-order-status-${String(
                            order.status || "unknown"
                          ).toLowerCase()}`}
                        >
                          {order.status || "UNKNOWN"}
                        </span>
                      </td>

                      <td>
                        {order.createdAt?.toDate
                          ? order.createdAt
                              .toDate()
                              .toLocaleString()
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="bulk-panel">
          <div className="bulk-panel-heading">
            <h2>Recent Digits</h2>
            <span>
              {digits.length} / 100
            </span>
          </div>

          <div className="bulk-digits">
            {digits.length === 0 ? (
              <span className="bulk-muted">
                Inasubiri live ticks...
              </span>
            ) : (
              digits.slice(0, 30).map((digit, index) => (
                <span
                  key={`${index}-${digit}`}
                  className={`bulk-digit ${
                    digit === prediction
                      ? "bulk-digit-highlight"
                      : ""
                  }`}
                  title={`Digit ${digit}`}
                >
                  {digit}
                </span>
              ))
            )}
          </div>
        </section>
      </main>

      <BottomNavVolatility />
    </div>
  );
}